import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

(()=>{
  if(location.pathname==='/search')document.documentElement.classList.add('cm-search-processing');
  const clean=(value)=>(value||'')
    .toLocaleLowerCase('it')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-z0-9]+/g,' ')
    .trim();

  const handleize=(value)=>(value||'')
    .toLocaleLowerCase('it')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g,'')
    .replace(/['’]/g,'')
    .replace(/[^a-z0-9]+/g,'-')
    .replace(/^-|-$/g,'');

  const provinceHandles=new Set(['agrigento','alessandria','ancona','aosta','arezzo','ascoli-piceno','asti','avellino','bari','belluno','benevento','bergamo','biella','bologna','bolzano','brescia','brindisi','cagliari','caltanissetta','campobasso','caserta','catania','catanzaro','chieti','como','cosenza','cremona','crotone','cuneo','enna','ferrara','firenze','foggia','forli','frosinone','genova','gorizia','grosseto','imperia','isernia','laquila','la-spezia','latina','lecce','lecco','livorno','lodi','lucca','macerata','mantova','massa','matera','messina','milano','modena','napoli','novara','nuoro','oristano','padova','palermo','parma','pavia','perugia','pesaro','piacenza','pisa','pistoia','pordenone','potenza','prato','ragusa','ravenna','reggio-calabria','reggio-emilia','rieti','rimini','roma','rovigo','salerno','sassari','savona','siena','siracusa','sondrio','taranto','terni','torino','trapani','trento','treviso','trieste','udine','urbino','varese','venezia','verbania','vercelli','verona','vibo-valentia','vicenza','viterbo']);

  const make=(tag,text,className)=>{
    const element=document.createElement(tag);
    element.className=className;
    element.textContent=text;
    return element;
  };

  const phrasePosition=(titleWords,queryWords)=>{
    if(!queryWords.length||queryWords.length>titleWords.length)return -1;
    for(let index=0;index<=titleWords.length-queryWords.length;index+=1){
      if(queryWords.every((word,offset)=>titleWords[index+offset]===word))return index;
    }
    return -1;
  };

  const addSearchButton=()=>{
    document.querySelectorAll('form.search-page-input__parent').forEach((form)=>{
      const input=form.querySelector('input[name="q"]');
      if(!input)return;
      const component=form.querySelector('search-page-input-component');
      if(component&&!component.querySelector('.cm-search-scope')){
        const params=new URLSearchParams(location.search);
        const scope=(params.get('cm_scope')||'').trim();
        const select=document.createElement('select');
        select.className='cm-search-scope';
        select.name='cm_scope';
        select.setAttribute('aria-label','Ambito della ricerca');
        select.append(new Option('Tutto il negozio',''));
        if(scope)select.append(new Option(scope,scope,true,true));
        component.insertBefore(select,input);
      }
      if(form.querySelector('.cm-search-submit'))return;
      const button=document.createElement('button');
      button.type='submit';
      button.className='cm-search-submit';
      button.textContent='Cerca';
      const reset=component?.querySelector('.search__reset-button');
      if(component)component.insertBefore(button,reset||null);
      else form.appendChild(button);
    });
  };

  const guardResetButton=()=>{
    if(window.cmSearchResetGuard)return;
    window.cmSearchResetGuard=true;
    document.addEventListener('click',(event)=>{
      const target=event.target instanceof Element?event.target.closest('.search-page-input__parent .search__reset-button'):null;
      if(!target)return;
      const form=target.closest('form');
      const input=form&&form.querySelector('input[name="q"]');
      if(!input)return;
      event.preventDefault();
      event.stopImmediatePropagation();
      input.value='';
      input.setAttribute('value','');
      input.dispatchEvent(new Event('input',{bubbles:true}));
      input.focus();
    },true);
  };

  const collectFallbackPages=async(grid,queryWords,scopeWords,params)=>{
    if(grid.dataset.cmCollected==='complete'){document.documentElement.classList.remove('cm-search-processing');return;}
    if(grid.dataset.cmCollected==='loading')return;
    grid.dataset.cmCollected='loading';
    try{
      const pageNumbers=[...document.querySelectorAll('.pagination a[href*="page="]')]
        .map((link)=>Number(new URL(link.href,location.origin).searchParams.get('page'))||1);
      const lastPage=Math.min(Math.max(1,...pageNumbers),20);
      const currentPage=Number(params.get('page'))||1;
      const known=new Set([...grid.querySelectorAll('a[href*="/products/"]')].map((link)=>new URL(link.href,location.origin).pathname));
      for(let page=1;page<=lastPage;page+=1){
        if(page===currentPage)continue;
        const pageUrl=new URL(location.href);
        pageUrl.searchParams.set('page',String(page));
        const html=await fetch(pageUrl,{credentials:'same-origin'}).then((response)=>response.text());
        const doc=new DOMParser().parseFromString(html,'text/html');
        [...doc.querySelectorAll('#ResultsList .product-grid__item')].forEach((card)=>{
          const title=clean(card.querySelector('h3')?.textContent||'');
          const words=title.split(' ');
          if(!queryWords.every((word)=>words.includes(word))||!scopeWords.every((word)=>words.includes(word)))return;
          const productLink=card.querySelector('a[href*="/products/"]');
          const key=productLink?new URL(productLink.href,location.origin).pathname:title;
          if(known.has(key))return;
          known.add(key);
          grid.appendChild(document.importNode(card,true));
        });
      }
      const total=grid.querySelectorAll('.product-grid__item').length;
      const status=[...document.querySelectorAll('[role="status"]')].find((element)=>/\barticol[oi]\b/i.test(element.textContent));
      if(status)status.textContent=`${total} ${total===1?'articolo':'articoli'}`;
      const pagination=document.querySelector('results-list .pagination');
      if(pagination)pagination.hidden=true;
      grid.dataset.cmCollected='complete';
    }catch(error){
      grid.dataset.cmCollected='complete';
    }
    document.documentElement.classList.remove('cm-search-processing');
  };

  const run=()=>{
    addSearchButton();
    const params=new URLSearchParams(location.search);
    const rawTerm=(params.get('q')||'').trim();
    if(!rawTerm){document.documentElement.classList.remove('cm-search-processing');return;}

    const advancedMatch=rawTerm.match(/^title:["“](.+)["”]$/i);
    if(params.get('cm_exact')!=='1'&&!advancedMatch){
      const original=rawTerm.replace(/["“”]/g,'').trim();
      if(!original)return;
      const originalWords=clean(original).split(' ').filter(Boolean);
      if(originalWords.length>=3){
        params.set('q',original);
        params.set('cm_fallback','1');
        params.set('view','96');
        params.delete('options[prefix]');
      }else{
        params.set('q',`title:"${original}"`);
        params.set('options[prefix]','none');
        params.delete('cm_fallback');
      }
      params.set('type','product');
      params.set('cm_exact','1');
      params.set('cm_q',original);
      params.delete('page');
      location.replace(`${location.pathname}?${params.toString()}`);
      return;
    }

    const original=(params.get('cm_q')||advancedMatch?.[1]||rawTerm).trim();
    const query=clean(original);
    if(!query)return;

    const root=document.querySelector('results-list');
    const grid=root&&root.querySelector('#ResultsList');
    if(!grid)return;
    const cards=[...grid.querySelectorAll('.product-grid__item')];
    if(!cards.length){
      if(params.get('cm_exact')==='1'&&params.get('cm_fallback')!=='1'){
        params.set('q',original);
        params.set('cm_fallback','1');
        params.set('view','96');
        params.delete('options[prefix]');
        params.delete('page');
        location.replace(`${location.pathname}?${params.toString()}`);
      }
      if(params.get('cm_fallback')==='1')document.documentElement.classList.remove('cm-search-processing');
      return;
    }

    const queryWords=query.split(' ');
    const scopeWords=clean(params.get('cm_scope')||'').split(' ').filter(Boolean);
    const fallback=params.get('cm_fallback')==='1';
    const compactQuery=query.replace(/\s+/g,'');
    const isSku=/^[a-z]{2,4}\d{4}$/.test(compactQuery);
    const cardData=cards.map((card)=>{
      const title=clean(card.querySelector('h3')?.textContent||'');
      const words=title.split(' ');
      return {card,title,words,position:phrasePosition(words,queryWords)};
    });
    const hasGeographicAnchor=!isSku&&cardData.some(({words,position})=>
      position>=1&&position<=2&&provinceHandles.has(handleize(words[0]))
    );
    const exact=cardData.filter(({title,words,position})=>{
      const inScope=!scopeWords.length||scopeWords.every((word)=>words.includes(word));
      if(!inScope)return false;
      if(isSku)return title.split(' ').includes(compactQuery);
      if(fallback){
        if(!queryWords.every((word)=>words.includes(word)))return false;
        if(hasGeographicAnchor)return position>=1&&position<=2&&provinceHandles.has(handleize(words[0]));
        return true;
      }
      if(hasGeographicAnchor)return position>=1&&position<=2&&provinceHandles.has(handleize(words[0]));
      return position>=0;
    });

    cardData.forEach(({card})=>{
      if(!exact.some((item)=>item.card===card))card.remove();
    });
    grid.dataset.cmGrouped=query;

    document.querySelectorAll('input[name="q"]').forEach((input)=>{
      input.value=original;
      input.setAttribute('value',original);
    });

    grid.querySelectorAll('a[href*="/products/"]').forEach((link)=>{
      const url=new URL(link.href,location.origin);
      url.searchParams.set('cm_return',`${location.pathname}${location.search}`);
      link.href=url.toString();
    });

    const pagination=root.querySelector('.pagination');
    if(fallback&&pagination)pagination.hidden=true;
    const status=[...document.querySelectorAll('[role="status"]')]
      .find((element)=>/\barticol[oi]\b/i.test(element.textContent));
    if(status){
      const countLabel=`${exact.length} ${exact.length===1?'articolo':'articoli'}`;
      if(status.textContent.trim()!==countLabel)status.textContent=countLabel;
    }

    if(pagination){
      pagination.querySelectorAll('a[href]').forEach((link)=>{
        const pageUrl=new URL(link.href,location.origin);
        ['cm_exact','cm_q','cm_fallback','cm_scope','view'].forEach((key)=>{
          if(params.has(key))pageUrl.searchParams.set(key,params.get(key));
        });
        link.href=pageUrl.toString();
      });
    }

    if(!grid.parentNode.querySelector('.cm-search-heading'))grid.parentNode.insertBefore(make('h2',fallback?'Corrispondenze con tutte le parole':'Corrispondenze esatte','cm-search-heading'),grid);
    if(fallback)collectFallbackPages(grid,queryWords,scopeWords,params);
    else document.documentElement.classList.remove('cm-search-processing');
  };

  const addReturnLink=()=>{
    if(!location.pathname.includes('/products/')||document.querySelector('.cm-return-search'))return;
    const returnPath=new URLSearchParams(location.search).get('cm_return');
    if(!returnPath||!returnPath.startsWith('/search?'))return;
    const anchor=document.createElement('a');
    anchor.className='cm-return-search';
    anchor.href=returnPath;
    anchor.textContent='← Torna ai risultati della ricerca';
    const target=document.querySelector('main h1, main .product-information, main');
    if(target)target.parentNode.insertBefore(anchor,target);
  };

  const ensureFallback=()=>{
    const params=new URLSearchParams(location.search);
    if(params.get('cm_exact')!=='1'||params.get('cm_fallback')==='1')return;
    const grid=document.querySelector('#ResultsList');
    if(!grid||grid.querySelector('.product-grid__item'))return;
    const original=(params.get('cm_q')||'').trim();
    if(!original)return;
    params.set('q',original);
    params.set('cm_fallback','1');
    params.set('view','96');
    params.delete('options[prefix]');
    params.delete('page');
    location.replace(`${location.pathname}?${params.toString()}`);
  };

  const addStyles=()=>{
    if(document.querySelector('#cm-search-improvements'))return;
    const style=document.createElement('style');
    style.id='cm-search-improvements';
    style.textContent=`
      .cm-search-heading{grid-column:1/-1;margin:28px 0 14px;font-size:clamp(1.35rem,2vw,1.8rem)}
      .cm-search-processing #ResultsList,.cm-search-processing .pagination,.cm-search-processing search-page-input-component p{visibility:hidden!important}
      .search-page-input__parent search-page-input-component{position:relative;width:100%!important;--size-style-width:100%!important}
      .search-page-input__parent .search-page-input{padding-inline-start:250px!important;padding-inline-end:160px!important}
      .cm-search-scope{position:absolute;inset-inline-start:48px;top:50%;z-index:2;width:185px;height:40px;padding:0 28px 0 10px;transform:translateY(-50%);border:0;border-inline-end:1px solid rgba(var(--color-foreground),.3);background:rgb(var(--color-background));color:rgb(var(--color-foreground));font:inherit;font-weight:700;cursor:pointer}
      .cm-search-submit{position:absolute;inset-inline-end:58px;top:50%;z-index:2;min-height:40px;margin:0;padding:0 18px;transform:translateY(-50%);border:1px solid rgb(var(--color-foreground));border-radius:7px;background:rgb(var(--color-foreground));color:rgb(var(--color-background));font:inherit;font-weight:700;cursor:pointer}
      .cm-return-search{display:block;width:max-content;max-width:calc(100% - 32px);margin:20px auto;padding:14px 20px;border:1px solid currentColor;border-radius:6px;color:rgb(var(--color-foreground));font-size:1.05rem;font-weight:700;text-decoration:none}
      @media(max-width:749px){.search-page-input__parent .search-page-input{padding-inline-start:190px!important;padding-inline-end:145px!important}.cm-search-scope{inset-inline-start:42px;width:138px;font-size:.9rem}.cm-search-submit{inset-inline-end:52px;min-height:38px;padding:0 14px}.cm-return-search{width:calc(100% - 32px);text-align:center}}
    `;
    document.head.appendChild(style);
  };

  const boot=()=>{addStyles();addSearchButton();guardResetButton();addReturnLink();run();clearTimeout(window.cmFallbackTimer);window.cmFallbackTimer=setTimeout(ensureFallback,350);};
  document.readyState==='loading'?document.addEventListener('DOMContentLoaded',boot):boot();
  new MutationObserver(()=>{clearTimeout(window.cmSearchRefreshTimer);window.cmSearchRefreshTimer=setTimeout(boot,180);}).observe(document.documentElement,{childList:true,subtree:true});
})();
